import { createRoute, Link, redirect } from '@tanstack/react-router';
import { useState } from 'react';
import { IconPlus, IconEdit, IconTrash, IconBook2 } from '@tabler/icons-react';
import {
  COURSE_CATEGORIES,
  DEFAULT_CATEGORY_LABELS,
  type CourseCategory,
} from '@admitly/shared';
import { Route as appRoute } from '../../../app.js';
import { useAuthStore } from '../../../../lib/auth.js';
import { useAdminCourses, useDeleteCourse } from '../../../../hooks/useAdminCourses.js';
import { AdminPageHeader } from '../../../../components/admin/AdminPageHeader.js';
import { Button } from '../../../../components/ui/Button.js';
import { Input } from '../../../../components/ui/Input.js';
import { Select } from '../../../../components/ui/Select.js';
import { centsToAud, formatDate } from '../../../../lib/format.js';

export const Route = createRoute({
  getParentRoute: () => appRoute,
  path: 'admin/courses',
  beforeLoad: () => {
    const role = useAuthStore.getState().user?.role;
    if (role !== 'super_admin') throw redirect({ to: '/app' });
  },
  component: CourseListPage,
});

const CURATION_LABELS: Record<string, string> = {
  shell: 'Shell',
  curated: 'Curated',
  needs_review: 'Needs review',
  archived: 'Archived',
};

const CURATION_TONE: Record<string, string> = {
  shell: 'bg-cream-deep text-muted',
  curated: 'bg-success-bg text-success',
  needs_review: 'bg-warn-bg text-warn',
  archived: 'bg-danger-bg text-danger',
};

function CourseListPage() {
  const [category, setCategory] = useState<CourseCategory | ''>('');
  const [q, setQ] = useState('');
  const { data, isLoading, error } = useAdminCourses({
    category: category || undefined,
    q: q || undefined,
  });
  const del = useDeleteCourse();

  async function handleDelete(id: string, name: string) {
    if (!confirm(`Archive course "${name}"?`)) return;
    try {
      await del.mutateAsync(id);
    } catch (e) {
      alert((e as Error).message);
    }
  }

  return (
    <div>
      <AdminPageHeader
        title="Courses"
        description="The master catalogue. Click a row to edit; tenant overlays come in Phase 2."
        actions={
          <Link to="/app/admin/courses/new">
            <Button leftIcon={<IconPlus size={16} />}>New course</Button>
          </Link>
        }
      />

      <div className="mb-6 flex flex-wrap gap-3 max-w-3xl">
        <div className="flex-1 min-w-[260px]">
          <Input
            placeholder="Search by course name, CRICOS, or provider…"
            value={q}
            onChange={(e) => setQ(e.target.value)}
          />
        </div>
        <div className="w-56">
          <Select value={category} onChange={(e) => setCategory(e.target.value as CourseCategory)}>
            <option value="">All categories</option>
            {COURSE_CATEGORIES.map((c) => (
              <option key={c} value={c}>
                {DEFAULT_CATEGORY_LABELS[c]}
              </option>
            ))}
          </Select>
        </div>
      </div>

      {isLoading && <p className="text-muted">Loading…</p>}
      {error && (
        <p className="text-danger bg-danger-bg border border-danger/30 rounded-lg px-4 py-3">
          Couldn't load courses: {(error as Error).message}
        </p>
      )}

      {data && data.items.length === 0 && (
        <div className="border border-dashed border-line-strong rounded-xl p-12 text-center bg-white/40">
          <IconBook2 size={32} className="text-muted-soft mx-auto mb-3" />
          <p className="text-ink-soft font-medium">No courses match</p>
          <p className="text-sm text-muted mt-1">Adjust filters or add a new course.</p>
        </div>
      )}

      {data && data.items.length > 0 && (
        <div className="bg-white border border-line rounded-xl overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-cream/60 border-b border-line">
              <tr className="text-left text-muted text-xs uppercase tracking-wider">
                <th className="px-5 py-3 font-medium">Course</th>
                <th className="px-5 py-3 font-medium">Provider</th>
                <th className="px-5 py-3 font-medium">CRICOS</th>
                <th className="px-5 py-3 font-medium">Category</th>
                <th className="px-5 py-3 font-medium">Annual fee</th>
                <th className="px-5 py-3 font-medium">State</th>
                <th className="px-5 py-3 font-medium">Verified</th>
                <th className="px-5 py-3 font-medium">Updated</th>
                <th className="px-5 py-3 font-medium text-right">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-line">
              {data.items.map((c) => (
                <tr key={c.id} className="hover:bg-cream/30 transition-colors">
                  <td className="px-5 py-3.5">
                    <Link
                      to="/app/admin/courses/$courseId"
                      params={{ courseId: c.id }}
                      className="font-medium text-ink hover:text-navy"
                    >
                      {c.name}
                    </Link>
                  </td>
                  <td className="px-5 py-3.5 text-ink-soft">
                    {c.providerShortName ?? c.providerName}
                  </td>
                  <td className="px-5 py-3.5 font-mono text-xs text-ink-soft">
                    {c.cricosCode ?? '—'}
                  </td>
                  <td className="px-5 py-3.5 text-ink-soft text-xs">
                    {DEFAULT_CATEGORY_LABELS[c.category]}
                  </td>
                  <td className="px-5 py-3.5 text-ink-soft">
                    {centsToAud(c.annualTuitionCents)}
                  </td>
                  <td className="px-5 py-3.5">
                    <span
                      className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${CURATION_TONE[c.curationState] ?? ''}`}
                    >
                      {CURATION_LABELS[c.curationState] ?? c.curationState}
                    </span>
                  </td>
                  <td className="px-5 py-3.5 text-xs">
                    {c.verifiedAt ? (
                      <span className="inline-flex items-center px-2 py-0.5 rounded font-medium bg-success-bg text-success">
                        {formatDate(c.verifiedAt)}
                      </span>
                    ) : (
                      <span className="text-muted-soft">—</span>
                    )}
                  </td>
                  <td className="px-5 py-3.5 text-muted text-xs">{formatDate(c.updatedAt)}</td>
                  <td className="px-5 py-3.5 text-right">
                    <div className="inline-flex items-center gap-1">
                      <Link
                        to="/app/admin/courses/$courseId"
                        params={{ courseId: c.id }}
                        className="p-1.5 rounded hover:bg-cream-deep text-muted hover:text-navy"
                        aria-label="Edit"
                      >
                        <IconEdit size={16} />
                      </Link>
                      <button
                        onClick={() => handleDelete(c.id, c.name)}
                        className="p-1.5 rounded hover:bg-danger-bg text-muted hover:text-danger"
                        aria-label="Archive"
                      >
                        <IconTrash size={16} />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
